// Lo que la ficha 360 del cliente DERIVA de `GET /customers/{id}/summary360`. Todo lo de aquí es
// puro: la lectura vive en `api/useCustomer360` y el pintado en los componentes de la ficha.
import type { RetailKycStatus } from '@/api/services/retail-customer/retail-customer.client';
import type { Gap } from './customer360Gaps';

/** Los primeros diez de la CURP: cuatro letras del nombre y la fecha de nacimiento. */
const RFC_BASE_LENGTH = 10;

/**
 * El RFC BASE de la CURP. Es la parte que el SAT construye igual en los dos documentos; la
 * homoclave de tres caracteres no se puede derivar, así que nunca sale de aquí.
 */
export function rfcBaseOf(curp: string | null | undefined): string | null {
    if (!curp || curp.length < RFC_BASE_LENGTH) return null;
    return curp.slice(0, RFC_BASE_LENGTH).toUpperCase();
}

export const RFC_BASE_NOTE =
    'Derivado de los primeros diez caracteres de la CURP: cuatro letras del nombre y la fecha de nacimiento. Es el RFC BASE — le falta la homoclave de tres caracteres que asigna el SAT, y esa no se puede calcular.';

/** La edad del cliente: sabida, sin fecha de nacimiento, o con una fecha que no da una edad. */
export type CustomerAge =
    | { state: 'known'; years: number }
    | { state: 'no-birth-date' }
    | { state: 'invalid' };

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Años cumplidos al día de `now`. La fecha de nacimiento es un día de calendario suelto, así que se
 * compara por partes: convertirla de zona la corre un día en cualquier navegador al este de CDMX.
 */
export function ageOf(birthDate: string | null | undefined, now: Date): CustomerAge {
    if (!birthDate) return { state: 'no-birth-date' };
    const match = DAY_PATTERN.exec(birthDate);
    if (!match) return { state: 'invalid' };

    const [birthYear, birthMonth, birthDay] = match.slice(1).map(Number);
    let years = now.getFullYear() - birthYear;
    const monthDelta = now.getMonth() + 1 - birthMonth;
    if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < birthDay)) years -= 1;

    return years < 0 ? { state: 'invalid' } : { state: 'known', years };
}

export const NO_BIRTH_DATE: Gap = {
    label: 'Sin fecha de nacimiento',
    note: 'El alta de este cliente no registró fecha de nacimiento, así que no hay edad que calcular. El KYC la trae cuando alcanza a leerla del documento.',
};

export const INVALID_BIRTH_DATE: Gap = {
    label: 'Fecha inválida',
    note: 'La fecha de nacimiento registrada no da una edad posible: está en el futuro o no tiene forma de fecha. El dato está mal capturado, no ausente.',
};

/**
 * El catálogo del proveedor de KYC, nombrado en español. Puede crecer sin avisarnos: un tipo que no
 * conocemos se muestra CRUDO, que es feo pero verdadero — inventarle nombre sería peor.
 */
const DOCUMENT_TYPE_LABEL: Record<string, string> = {
    'national-id': 'Credencial para votar (INE)',
    passport: 'Pasaporte',
    'driving-license': 'Licencia de conducir',
    'foreign-id': 'Documento de identidad extranjero',
};

export function documentTypeLabel(documentType: string | null | undefined): string | null {
    if (!documentType) return null;
    return DOCUMENT_TYPE_LABEL[documentType] ?? documentType;
}

/* ================================================================================
   VEREDICTO DE KYC — cinco estatus, no un interruptor de dos posiciones.
   ================================================================================ */

/** Solo `VERIFIED` es aprobado. Colapsar los otros cuatro en «Falló» rechazaría a quien no falló. */
export const KYC_STATUS_LABEL: Record<RetailKycStatus, string> = {
    VERIFIED: 'Aprobado',
    FAILED: 'Falló',
    PENDING: 'Sin veredicto',
    MANUAL: 'Revisión manual',
    REVIEW: 'En revisión',
};

export const KYC_STATUS_CHIP_CLASS: Record<RetailKycStatus, string> = {
    VERIFIED: 'border-success bg-success/10 text-success',
    FAILED: 'border-destructive bg-destructive/10 text-destructive',
    PENDING: 'border-border bg-muted text-muted-foreground',
    MANUAL: 'border-warning bg-warning/10 text-warning',
    REVIEW: 'border-warning bg-warning/10 text-warning',
};

/** Qué le pide cada estatus a quien lo lee. Los cinco piden cosas distintas. */
export const KYC_STATUS_NOTE: Record<RetailKycStatus, string> = {
    VERIFIED:
        'El antifraude de MetaMap aprobó a este cliente. Es un solo veredicto: no dice cuál de las ocho verificaciones pasó, ni con cuánto margen.',
    FAILED: 'El antifraude de MetaMap rechazó a este cliente. El veredicto no dice cuál verificación falló: ese detalle se queda en la sesión de venta del wizard.',
    PENDING:
        'MetaMap todavía no devolvió veredicto: la verificación quedó abierta o el cliente nunca la terminó. No es un rechazo.',
    MANUAL: 'El veredicto automático no alcanzó y el caso pasó a captura manual. Está esperando que Cumplimiento lo resuelva.',
    REVIEW: 'El caso está en revisión de Cumplimiento. Quién lo tomó y con qué criterio lo resolvió no lo devuelve todavía ningún endpoint.',
};

export const NO_KYC_STATUS: Gap = {
    label: 'La ficha llegó sin estatus',
    note: 'La ficha de este cliente llegó sin estatus de KYC, que es un campo obligatorio del silo. La lectura salió bien: el dato es el que falta.',
};

/* ================================================================================
   CUENTA PAGOCONFIADO — lo poco que sí se puede afirmar hoy.
   ================================================================================ */

/** El emisor de la CLABE es siempre el mismo: no depende del cliente ni viaja en la respuesta. */
export const PCF_INSTITUTION_NAME = 'STP · Sistema de Transferencias y Pagos';
